import * as cookie from 'cookie'
import leafletStyleSheetUrl from 'leaflet/dist/leaflet.css?url'
import React from 'react'
import {
	data,
	type HeadersFunction,
	Links,
	type LinksFunction,
	type LoaderFunctionArgs,
	Meta,
	type MetaFunction,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import {
	COOKIE_NAME,
	type CookiePreferences,
} from '#app/utils/cookie-consent.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { useTheme } from './routes/resources+/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { getBetaBannerDismissed } from './utils/beta-banner.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { getTheme, type Theme } from './utils/theme.server.ts'
import {
	getServerTimeHeader,
	makeTimings,
	time,
} from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'

export const links: LinksFunction = () => {
	return [
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const,
		{ rel: 'stylesheet', href: leafletStyleSheetUrl, as: 'style' },
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ loaderData }) => {
	return [
		{
			title: loaderData
				? import.meta.env.VITE_APP_TITLE
				: `Error | ${import.meta.env.VITE_APP_TITLE}`,
		},
		{ name: 'description', content: import.meta.env.VITE_APP_DESCRIPTION },
	]
}

export async function loader({ request, url }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							username: true,
							email: true,
							image: { select: { id: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	let cookieConsent: CookiePreferences = {
		preferences: false,
		statistics: false,
		marketing: false,
	}
	const cookieHeader = request.headers.get('cookie')

	if (cookieHeader) {
		const consentCookie = cookie.parseCookie(cookieHeader)[COOKIE_NAME]
		if (consentCookie) {
			try {
				cookieConsent = JSON.parse(
					decodeURIComponent(consentCookie),
				) as CookiePreferences
			} catch {
				cookieConsent = {
					preferences: false,
					statistics: false,
					marketing: false,
				}
			}
		}
	}

	return data(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: url.pathname,
				userPrefs: {
					betaBannerDismissed: getBetaBannerDismissed(request),
					theme: getTheme(request),
					cookieConsent,
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': getServerTimeHeader(timings) },
				toastHeaders,
			),
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	return {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
}

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
	allowIndexing = true,
	statisticsConsent = false,
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Partial<ReturnType<typeof getEnv>>
	allowIndexing?: boolean
	statisticsConsent?: boolean
}) {
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta property="og:type" content="website" />
				<meta
					property="og:image"
					content="https://www.trailviewr.com/img/og-default.png"
				/>
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:site" content="@TrailViewr" />
				<meta
					name="twitter:image"
					content="https://www.trailviewr.com/img/twitter-card.png"
				/>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}

				<Links nonce={nonce} />
				{env.MODE === 'production' && statisticsConsent ? (
					<>
						<script
							async
							nonce={nonce}
							src="https://www.googletagmanager.com/gtag/js?id=G-9P077P51JX"
						/>
						<script nonce={nonce}>
							{`window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', 'G-9P077P51JX');`}
						</script>
					</>
				) : null}
			</head>
			<body className="bg-background text-foreground">
				{children}
				<EpicToaster closeButton position="top-center" theme={theme} />
				<EpicProgress />
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()
	const theme = useTheme()
	const allowIndexing = data.ENV.ALLOW_INDEXING !== 'false'
	useToast(data.toast)

	return (
		<Document
			nonce={nonce}
			theme={theme || 'light'}
			allowIndexing={allowIndexing}
			env={data.ENV}
			statisticsConsent={data.requestInfo.userPrefs.cookieConsent.statistics}
		>
			<Outlet context={{ theme: data.requestInfo.userPrefs.theme }} />
		</Document>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

export function ErrorBoundary() {
	const nonce = useNonce()

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>
	)
}
